/**
 * Settings → Billing & usage's meters, read from `usage.current` — `screens.md` §9 and
 * `components.md` → Usage meter. Two meters: `api_calls` has no writer — plan §0.14.
 */
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";

import type { Tier, UsageView } from "@packagepage/shared";
import { Surface, SurfaceBody, SurfaceHeader } from "@packagepage/ui";

import { api } from "../api/procedures.js";
import {
  METER_LABELS,
  METER_ORDER,
  countedFromOf,
  meterNote,
  readMeter,
} from "./usage-meter.js";

import "./usage-meters.css";

/** `October 2026` — the window, as the meter card names it. */
export function windowName(window: string): string {
  const [year, month] = window.split("-").map(Number);
  return new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, 1)).toLocaleDateString(
    "en-GB",
    {
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    },
  );
}

/** The card for one answer. Exported for the static suite. */
export function UsageMeters({
  usage,
  plan,
}: {
  readonly usage: UsageView;
  readonly plan: Tier;
}): ReactNode {
  const meters = METER_ORDER.flatMap((metric) =>
    usage.counters.filter((counter) => counter.metric === metric).map(readMeter),
  );
  const countedFrom = countedFromOf(usage);
  return (
    <Surface sectioned data-card="usage">
      <SurfaceHeader title="Usage" />
      <SurfaceBody>
        <div className="pp-usage">
          <p className="pp-usage__window">This month · {windowName(usage.window)}</p>
          {meters.length === 0 ? (
            <p className="pp-usage__note" data-usage-empty>
              Nothing has been counted this month yet. Counts update every hour.
            </p>
          ) : (
            meters.map((meter) => (
              <div
                key={meter.metric}
                className="pp-usage__meter"
                data-metric={meter.metric}
                data-state={meter.state}
              >
                <div className="pp-usage__head">
                  <span>{METER_LABELS[meter.metric]}</span>
                  <span className="pp-usage__figure">{meter.figure}</span>
                </div>
                <div
                  className="pp-usage__track"
                  role="meter"
                  aria-label={METER_LABELS[meter.metric]}
                  aria-valuenow={meter.value}
                  aria-valuemin={0}
                  aria-valuemax={meter.limit ?? undefined}
                >
                  <div
                    className="pp-usage__fill"
                    style={{ width: `${String(meter.fill)}%` }}
                  />
                </div>
                <p className="pp-usage__note">
                  {meterNote(meter.metric, plan, countedFrom)}
                </p>
              </div>
            ))
          )}
        </div>
      </SurfaceBody>
    </Surface>
  );
}

export function UsageCard({ plan }: { readonly plan: Tier }): ReactNode {
  const usage = useQuery({ queryKey: ["usage", "current"], queryFn: () => api.usage() });
  if (usage.isPending) return null;
  if (usage.isError) {
    return (
      <p className="pp-settings__error" role="alert">
        We could not load this month&apos;s usage. Reload to try again.
      </p>
    );
  }
  return <UsageMeters usage={usage.data} plan={plan} />;
}
